import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchBomCandidates, fetchDrawingPartNo, setDrawingPartNo,
} from '../api/client';

/**
 * 图纸工作台 · 标题栏那行的「零件」栏（v0.38.1 → **v0.83.1 重写**）。
 *
 * 为什么重写
 * ---------
 * v0.82 把本地台账 `part_master` 的写入口全部封成 410 Gone（物料唯一来源 = BOM），
 * 连带把本组件依赖的三个动作一起废了：归户 / 解除归户 / 用本图新建零件。
 * 而「图纸 → 物料 → 工艺路线 → 工序 SIP → 检验任务」整条链的第一个动作就是**归户**，
 * 所以必须换一条能走的路：
 *
 *   旧：`PATCH /drawing/{id}/part-master`（写台账外键）
 *   新：`PATCH /drawing/{id}/part-no`（直接把 **BOM 物料编码** 写进 `drawings.part_no`）
 *
 * 三条口径
 * --------
 * 1. **不建物料**。面板里没有「新建」——物料只来自 BOM 平台。
 * 2. **不猜**。搜不到就让他换关键词，不自动选一条最像的。
 * 3. **一料一码**：`part_no` 存的是 `bom_part_ref.material_code`，
 *    客户零件号只是输入的别名（后端会归一成物料编码）。
 */
export function DrawingPartChip({ drawingId, onToast }: {
  drawingId: number;
  /** @deprecated 台账废弃后不再使用；保留在签名里避免调用方大改。 */
  pageIndex?: number;
  /** @deprecated 同上。 */
  partMasterId?: number | null;
  /** @deprecated 同上 —— 已归户时不提供「到台账查看」入口。 */
  onOpenPart?: (pmId: number) => void;
  onToast: (m: string, t?: 'success' | 'error') => void;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const boxRef = useRef<HTMLSpanElement>(null);

  const infoQ = useQuery({
    queryKey: ['drawing-part-no', drawingId],
    queryFn: () => fetchDrawingPartNo(drawingId),
    refetchOnWindowFocus: false,
  });
  const info = infoQ.data;

  // 面板打开时才搜 BOM（3819 条，不必每次拉全量）
  const candQ = useQuery({
    queryKey: ['bom-candidates', drawingId, q],
    queryFn: () => fetchBomCandidates(drawingId, q),
    enabled: open,
    refetchOnWindowFocus: false,
  });
  const items = candQ.data?.items ?? [];

  // 点面板外收起（同旧实现：mousedown + contains，不用 blur ——
  //   blur 会在「输入框聚焦 → 点候选」时先触发，按钮点不动）
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['drawing-part-no', drawingId] });
    qc.invalidateQueries({ queryKey: ['drawing', drawingId] });
    qc.invalidateQueries({ queryKey: ['drawings'] });
    qc.invalidateQueries({ queryKey: ['pm-drawings'] });
    qc.invalidateQueries({ queryKey: ['route-ops'] });
    qc.invalidateQueries({ queryKey: ['folders'] });
  };

  const apply = async (code: string | null, label: string) => {
    setBusy(true);
    try {
      const r = await setDrawingPartNo(drawingId, code);
      onToast(code ? `已归户到物料：${label}` : '已解除归户（图纸保留）', 'success');
      refresh();
      setOpen(false);
      setQ('');
      // 归户后工序清单要跟着变（工序来自物料的 BOM 工艺路线）
      if (r.part_no) {
        qc.invalidateQueries({ queryKey: ['route-ops'] });
      }
    } catch (e: any) {
      onToast(e.message, 'error');
    } finally { setBusy(false); }
  };

  const m = info?.material;
  const unbound = info ? !info.bound : false;
  const chipLabel = info?.bound
    ? `物料：${info.part_no}${m?.material_name ? ` · ${m.material_name}` : ''}`
    // 未归户时把「下一步动作」写进按钮本身 —— 原来只写「未归户」，
    // 混在「3 页 / 595×842pt / 218 个特征」里像个状态标签，没人会去点它。
    : '未归户 · 点此挂物料';

  return (
    <span className={`dp-part${info?.bound ? ' on' : ''}`} ref={boxRef}>
      <button className="dp-chip" onClick={() => setOpen((v) => !v)}
              /* 未归户 = 橙底橙框（醒目但不喧哗）。灰底小签传达不出
                 「这是你必须先做的一步」这层意思。 */
              style={unbound ? {
                background: '#faeeda', border: '1px solid #ef9f27',
                color: '#854f0b', fontWeight: 600,
              } : undefined}
              title={info?.bound
                ? `已归户到 BOM 物料 ${info.part_no}（本页可解除或改绑）`
                : '本图还没归户到物料 —— 点开搜 BOM 物料挂上'}>
        <span className="dp-chip-dot" />
        {infoQ.isLoading ? '读取中…' : chipLabel}
        <span className="dp-chip-care">▾</span>
      </button>

      {open && (
        <div className="dp-panel" onClick={(e) => e.stopPropagation()}>
          {info?.bound ? (
            <>
              <div className="dp-p-head">
                已归户到物料 <strong>{info.part_no}</strong>
                {m?.material_name ? `（${m.material_name}）` : ''}
              </div>
              <div className="dp-p-note">
                {m
                  ? <>BOM 里这个物料有 <b>{m.route_op_count}</b> 道工序
                      {m.specification ? ` ｜ 规格 ${m.specification}` : ''}
                      {m.material_type ? ` ｜ 类型 ${m.material_type}` : ''}</>
                  : '⚠ 这个编码在本地 BOM 镜像里查不到 —— 可能是同步后物料被改号了'}
              </div>
              <div className="dp-p-note">
                物料的名称 / 规格 / 工艺路线都由 BOM 平台维护，这里只能改「挂哪个物料」。
              </div>
              <div className="dp-p-row">
                <button className="btn sm danger" disabled={busy}
                        onClick={() => apply(null, '')}>
                  解除归户
                </button>
              </div>
              <div className="dp-p-sep">改成别的物料</div>
              <input className="pm-input" value={q}
                     placeholder="物料编码 / 零件号 / 名称"
                     onChange={(e) => setQ(e.target.value)} />
              <div className="dp-p-list">
                {items.filter((x) => x.material_code !== info.part_no).slice(0, 20)
                  .map((x) => (
                    <button key={x.material_code} className="dp-p-item" disabled={busy}
                            onClick={() => apply(x.material_code,
                              `${x.material_code}${x.material_name ? ` · ${x.material_name}` : ''}`)}>
                      {x.material_code}
                      {x.material_name ? ` · ${x.material_name}` : ''}
                    </button>
                  ))}
                {!candQ.isLoading && items.length === 0 && (
                  <div className="dp-p-note">BOM 里搜不到「{q}」</div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="dp-p-head">本图尚未归户到物料</div>
              <div className="dp-p-note">
                归户之后才会带出：BOM 工艺路线 → 工序 SIP → 检验任务。
                物料由 <b>BOM 平台</b>维护，这里只做「挂哪个」的选择，<b>不新建物料</b>。
              </div>
              <input className="pm-input" value={q} autoFocus
                     placeholder="输入物料编码 / 零件号 / 名称"
                     onChange={(e) => setQ(e.target.value)} />
              {candQ.isLoading && <div className="dp-p-note">正在搜 BOM…</div>}
              <div className="dp-p-list">
                {items.map((x) => (
                  <button key={x.material_code} className="dp-p-item" disabled={busy}
                          onClick={() => apply(x.material_code,
                            `${x.material_code}${x.material_name ? ` · ${x.material_name}` : ''}`)}>
                    {x.material_code}
                    {x.material_name ? ` · ${x.material_name}` : ''}
                    {x.specification ? ` · ${x.specification}` : ''}
                  </button>
                ))}
                {!candQ.isLoading && items.length === 0 && (
                  <div className="dp-p-note">
                    BOM 里搜不到「{q}」—— 物料要在 BOM 平台建好并同步过来
                    （每天 03:10 自动同步，也可手动跑 scripts/sync_bom.py）。
                  </div>
                )}
              </div>
            </>
          )}
          {/* ★ 面板里没有、也永远不会有「上传图纸」和「新建物料」 */}
        </div>
      )}
    </span>
  );
}
